import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import { Input } from '@components/Input';
import { Typography } from '@components/Typography';
import { MultiSelectLabels } from '@components/MultiSelectLabels';
import PublicLinkField from '@components/PublicLinkField';
import ComboLocationSelect from '@features/ComboLocationSelect';
import ContactFieldsSettings from '@features/ContactFieldsSettings';
import LocalizedTexts from '@features/LocalizeItemModal/LocalizedTexts';
import { useLocations, useTranslation } from '@hooks';
import { FORM_INTERNAL_NAME_MAX_LENGTH, FORM_TEXT_FIELDS } from '@constants/forms';
import { LabelType } from '@constants/labels';
import { getFormPublicUrl } from '../helpers';

const SectionTitle = ({ title, subtitle }) => (
    <>
        <Typography variant="subtitle1" sx={{ color: 'var(--text-primary)', marginTop: '16px' }}>
            {title}
        </Typography>
        <Typography variant="caption" sx={{ color: 'var(--text-secondary)', display: 'block', marginTop: '4px' }}>
            {subtitle}
        </Typography>
    </>
);

const Settings = ({ isView, selectedForm, brands, currentLocale }) => {
    const { t } = useTranslation();
    const { control, setValue, getValues } = useFormContext();
    const { getSelectLocationsOptions } = useLocations();
    const { data: labels } = useSelector(state => state.labels);

    const brandsOptions = useMemo(() => (brands || []).map(item => ({ value: item.id, label: item.title })), [brands]);
    const locationsOptions = useMemo(() => getSelectLocationsOptions(), []);
    const labelsOptions = useMemo(() => labels.map(item => ({ value: item.id, label: item.name })), [labels]);

    return (
        <>
            <div style={{ margin: '8px 0' }}>
                <Controller
                    control={control}
                    name="internalName"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('FormsPage.internalName')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message, { count: FORM_INTERNAL_NAME_MAX_LENGTH }) : null}
                            required
                            disabled={isView}
                            sx={{ marginBottom: '8px' }}
                        />
                    )}
                />
                {selectedForm?.id ? <PublicLinkField url={getFormPublicUrl(selectedForm.id)} /> : null}
                <ComboLocationSelect
                    brandsOptions={brandsOptions}
                    locationsOptions={locationsOptions}
                    module="microsites"
                    data={selectedForm}
                    disabled={isView}
                    isView={isView}
                />
            </div>
            <LocalizedTexts fields={FORM_TEXT_FIELDS} currentLocale={currentLocale} isView={isView} />
            <SectionTitle title={t('FormsPage.contactFields')} subtitle={t('FormsPage.contactFieldsSubtitle')} />
            <ContactFieldsSettings disabled={isView} requireIdentifyingField />
            <SectionTitle title={t('FeedbackSurveys.labelsTitle')} subtitle={t('FeedbackSurveys.labelsSubtitle')} />
            <div style={{ marginTop: 8 }}>
                <Controller
                    control={control}
                    name="labels"
                    render={({ field, ref }) => (
                        <MultiSelectLabels
                            {...field}
                            ref={ref}
                            onChange={value => setValue('labels', value, { shouldDirty: true })}
                            options={labelsOptions}
                            label={t('common.labels')}
                            fullWidth
                            disabled={isView}
                            type={LabelType.Contact}
                            hideNpsLabels
                            onCreateCallback={newLabel =>
                                setValue('labels', [...getValues('labels'), newLabel], { shouldDirty: true })
                            }
                        />
                    )}
                />
            </div>
        </>
    );
};

export default Settings;
